import type { GroomingQueueEntry, GroomingQueueState } from "@ttc-spectree/toolbox-contract";
import { GROOMING_QUEUE_PATH } from "@/lib/grooming/paths";
import { sessionName } from "@/lib/toolbox/notify";

/**
 * "AB#30412 is next in line" (epic PI-SPEC-SW-E290, feature
 * PI-SPEC-SW-1482, UX-PM-04, frame B4).
 *
 * SpecTree Toolbox decides where a single item goes: since PKG-PM-B it puts
 * an item groomed from One item or the reading pane after what is running
 * and ahead of waiting session items, and marks the entry `nextInLine` with
 * how many waiting entries it went ahead of (`wentAheadOf`). The pages only
 * read that from the queue. An older app leaves both fields out and appends
 * the item; the card then says the item's real place in Up next and leaves
 * the "went ahead" clause out.
 *
 * React-free; the card is `components/grooming/next-in-line-card.tsx`.
 */

/**
 * True when the app puts a single item next in line (PKG-PM-B). The same app
 * release always reports `keepingAwake` on the queue; an older app leaves it
 * out and appends single items at the end, so its pages must not promise the
 * queue jump. False before the queue is read and without a queue. Pure;
 * exported for tests.
 */
export function appPutsSingleItemsNextInLine(state: Pick<GroomingQueueState, "keepingAwake"> | null): boolean {
  return typeof state?.keepingAwake === "boolean";
}

/** Where one waiting entry stands, for the next-in-line card. */
export interface NextInLineSummary {
  entry: GroomingQueueEntry;
  /** 1-based place in Up next. */
  position: number;
  /**
   * True when it is first in Up next, so it starts when a run slot frees up.
   * The app's `nextInLine` flag alone is not enough: an earlier next-in-line
   * item, a draft or a run put back at the front can wait ahead of it, and the
   * headline and chip must agree with the sentence's place.
   */
  nextInLine: boolean;
  /** Waiting entries it went ahead of; null when the app does not say (an older app). */
  wentAheadOf: number | null;
  /** How many entries are running now. */
  running: number;
  /** "briefs" when every running entry is a brief, else "runs". */
  runningNoun: "briefs" | "runs";
  /**
   * True when every running entry belongs to a session run (an agenda batch)
   * of the waiting item's own project. The card names that project's session,
   * so runs of another project's session never get its date.
   */
  runningForSession: boolean;
  /** True while the queue is paused, so nothing starts until it carries on. */
  paused: boolean;
  /** Rough minutes until a run slot frees up; null without a typical run time or nothing running. */
  waitMinutes: number | null;
  /** How far the soonest-finishing run is through a typical run, 0-1; null without an estimate. */
  waitProgress: number | null;
}

export interface NextInLineOptions {
  /** The project's typical brief run time in seconds (`GET /grooming/projects/:project/session`). */
  typicalItemSeconds?: number | null;
  /** "Now" in ms; tests pin it. */
  now?: number;
}

/**
 * The rough wait: the typical run time minus how long the longest-running
 * entry has run (it should finish first), at least a minute. Null without a
 * typical run time or a readable start. Pure; exported for tests.
 */
export function roughWaitMinutes(
  running: readonly GroomingQueueEntry[],
  typicalItemSeconds: number | null | undefined,
  now: number = Date.now()
): number | null {
  const elapsed = longestElapsedSeconds(running, now);
  if (elapsed === null || typeof typicalItemSeconds !== "number" || typicalItemSeconds <= 0) return null;
  return Math.max(1, Math.round((typicalItemSeconds - elapsed) / 60));
}

/** ADO project names compare case-insensitively (as the queue's keys do). */
function sameProject(a: string, b: string): boolean {
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}

function longestElapsedSeconds(running: readonly GroomingQueueEntry[], now: number): number | null {
  let longest: number | null = null;
  for (const entry of running) {
    const start = entry.startedAt ? Date.parse(entry.startedAt) : Number.NaN;
    if (Number.isNaN(start)) continue;
    const seconds = Math.max(0, (now - start) / 1000);
    if (longest === null || seconds > longest) longest = seconds;
  }
  return longest;
}

/**
 * Where the entry `entryId` waits, or null when it is not in Up next (it is
 * running, finished, removed, or the queue is not read yet). Pure; exported
 * for tests.
 */
export function nextInLineSummary(
  state: GroomingQueueState | null,
  entryId: string | null,
  options: NextInLineOptions = {}
): NextInLineSummary | null {
  if (!state || !entryId) return null;
  const index = state.upNext.findIndex((candidate) => candidate.id === entryId);
  const entry = index >= 0 ? state.upNext[index] : undefined;
  if (!entry) return null;
  const now = options.now ?? Date.now();
  const position = index + 1;
  const { running } = state;
  const typical = options.typicalItemSeconds;
  const elapsed = longestElapsedSeconds(running, now);
  const firstToStart = position === 1;
  return {
    entry,
    position,
    nextInLine: firstToStart,
    wentAheadOf: typeof entry.wentAheadOf === "number" ? entry.wentAheadOf : null,
    running: running.length,
    runningNoun: running.length > 0 && running.every((r) => r.kind === "groom-item") ? "briefs" : "runs",
    runningForSession:
      running.length > 0 && running.every((r) => r.batchMode === "agenda" && sameProject(r.project, entry.project)),
    paused: state.status === "paused",
    waitMinutes: firstToStart ? roughWaitMinutes(running, typical, now) : null,
    waitProgress:
      elapsed !== null && typeof typical === "number" && typical > 0 ? Math.min(1, Math.max(0, elapsed / typical)) : null,
  };
}

/**
 * True for an entry the next-in-line card speaks for: a single item's brief
 * (One item, the reading pane), not an item of a session run, which keeps its
 * "Queued · #n" badge. Pure; exported for tests.
 */
export function showsNextInLineCard(entry: GroomingQueueEntry): boolean {
  if (entry.kind !== "groom-item") return false;
  return entry.nextInLine === true || entry.batchMode === "single" || entry.batchMode === null;
}

/** "AB#30412 is next in line", or the real place when other entries wait ahead of it. Pure; exported for tests. */
export function nextInLineHeadline(summary: NextInLineSummary): string {
  const name = summary.entry.itemId ? `AB#${summary.entry.itemId}` : "This brief";
  return summary.nextInLine ? `${name} is next in line` : `${name} is #${String(summary.position)} in the Queue`;
}

/** The chip on the card: "Next in line", or "Waiting" when the headline already names the place. Pure; exported for tests. */
export function nextInLineChip(summary: NextInLineSummary): string {
  return summary.nextInLine ? "Next in line" : "Waiting";
}

export interface NextInLineSentenceOptions {
  /** The running session's date (`YYYY-MM-DD`), for "Monday's session". */
  sessionDate?: string | null;
  /** "Today" for the session name; tests pin it. */
  today?: Date;
}

/**
 * Frame B4's sentence: "2 briefs for Monday's session are running. This one
 * starts as soon as one of them finishes — about 2 minutes — and went ahead
 * of the 5 session items still waiting. You'll get a notification when the
 * brief is ready." Parts that do not apply are left out: nothing running
 * says "It starts now.", no estimate drops "about n minutes", no (or a 0)
 * went-ahead count drops that clause. Pure; exported for tests.
 */
export function nextInLineSentence(summary: NextInLineSummary, options: NextInLineSentenceOptions = {}): string {
  const notify = "You'll get a notification when the brief is ready.";
  const ahead = summary.wentAheadOf ?? 0;
  const aheadClause =
    ahead > 0 ? `went ahead of the ${String(ahead)} session ${ahead === 1 ? "item" : "items"} still waiting` : null;

  if (summary.paused) {
    const start = "The queue is paused. This one starts when the queue carries on";
    return `${aheadClause ? `${start} and ${aheadClause}` : start}. ${notify}`;
  }

  const before = summary.position - 1;
  if (summary.running === 0) {
    if (before > 0) {
      return `This one starts after the ${String(before)} ${before === 1 ? "entry" : "entries"} ahead of it. ${notify}`;
    }
    return `It starts now. ${notify}`;
  }

  const noun = summary.running === 1 ? summary.runningNoun.replace(/s$/, "") : summary.runningNoun;
  const forSession = summary.runningForSession ? ` for ${sessionName(options.sessionDate, options.today)}` : "";
  const runningPart = `${String(summary.running)} ${noun}${forSession} ${summary.running === 1 ? "is" : "are"} running.`;

  let startPart: string;
  if (summary.nextInLine && before === 0) {
    startPart = summary.running === 1 ? "This one starts as soon as it finishes" : "This one starts as soon as one of them finishes";
    if (summary.waitMinutes !== null) {
      startPart += ` — about ${String(summary.waitMinutes)} ${summary.waitMinutes === 1 ? "minute" : "minutes"} —`;
    }
  } else {
    startPart = `This one starts after the ${String(before)} ${before === 1 ? "entry" : "entries"} ahead of it`;
  }
  const sentence = aheadClause ? `${startPart} and ${aheadClause}` : startPart;
  return `${runningPart} ${sentence}. ${notify}`;
}

/** The search parameter that names the Queue row to highlight. */
export const QUEUE_HIGHLIGHT_PARAM = "highlight";

/** The Queue page link that highlights one entry's row. */
export function queueHighlightPath(entryId: string): string {
  return `${GROOMING_QUEUE_PATH}?${QUEUE_HIGHLIGHT_PARAM}=${encodeURIComponent(entryId)}`;
}
